var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import load from "load-script";
import { getCurrentMagentoClient } from "@jti/magento";
import { CasAddressesApi, CasProfileApi, PgsApi } from "@jti/rest-api";
import { setShippingMethodsOnCart } from "./cartSlice/actions/setShippingMethodsOnCart";
import { fetchOrderDeliveryLimits } from "./checkout/fetchOrderDeliveryLimits";
import { getDefaultShippingMethods } from "./checkout/getDefaultShippingMethods";
import { getPGSToken } from "./checkout/getPGSToken";
import { placeOrder } from "./checkout/placeOrder";
export const fetchOrderAttributes = createAsyncThunk("checkout/fetchOrderAttributes", (isFreeTrial) => __awaiter(void 0, void 0, void 0, function* () {
    const magentoClient = getCurrentMagentoClient(isFreeTrial);
    return magentoClient.getOrderAttributes();
}));
export const fetchAddresses = createAsyncThunk("checkout/fetchAddresses", () => __awaiter(void 0, void 0, void 0, function* () {
    const casAddressesApi = CasAddressesApi.init();
    return casAddressesApi.getAddresses();
}));
export const fetchAccountContact = createAsyncThunk("checkout/fetchAccountContact", () => __awaiter(void 0, void 0, void 0, function* () {
    const casProfileApi = CasProfileApi.init();
    return casProfileApi.getProfile();
}));
export const getPgsPaymentMethods = createAsyncThunk("checkout/pgsPaymentMethods", () => __awaiter(void 0, void 0, void 0, function* () {
    const pgsApi = PgsApi.init();
    return yield pgsApi.getPaymentMethods();
}));
export const getPgsClient = createAsyncThunk("checkout/pgsClient", (_a) => __awaiter(void 0, [_a], void 0, function* ({ code, locale }) {
    const pgsApi = PgsApi.init();
    return yield pgsApi.getClient(code, locale);
}));
export const getPgsAdapter = createAsyncThunk("checkout/pgsAdapter", (_a) => __awaiter(void 0, [_a], void 0, function* ({ code, locale }) {
    const pgsApi = PgsApi.init();
    const pgsApiClient = yield pgsApi.getClient(code, locale);
    return {
        client: pgsApiClient,
        paymentCode: code,
    };
}));
export const loadPgsAdapter = createAsyncThunk("checkout/loadPgsAdapter", (adapterUrl) => __awaiter(void 0, void 0, void 0, function* () {
    const loadAction = (url) => new Promise((resolve, reject) => {
        load(url, function (error) {
            if (error === null) {
                return resolve(url);
            }
            reject(error);
        });
    });
    return yield loadAction(adapterUrl);
}));
export const createAddress = createAsyncThunk("checkout/createAddress", (_a) => __awaiter(void 0, [_a], void 0, function* ({ address }) {
    const casAddressesApi = CasAddressesApi.init();
    return casAddressesApi.createAddress(address);
}));
export const updateAgeVerification = createAsyncThunk("checkout/updateAgeVerification", (_a) => __awaiter(void 0, [_a], void 0, function* ({ avpUuid }) {
    const casProfileApi = CasProfileApi.init();
    return casProfileApi.updateAgeVerification(avpUuid);
}));
export const initialCheckoutState = {
    country: {
        countryName: "",
        countryCode: "",
    },
    addresses: {
        fetchStatus: "idle",
        data: [],
    },
    shippingMethods: {
        fetchStatus: "idle",
        data: [],
    },
    orderDeliveryLimits: {
        fetchStatus: "idle",
        data: { mode: "", limits: [] },
    },
    orderAttributes: {
        fetchStatus: "idle",
        data: [],
    },
    tradeInAttributes: undefined,
    profileDataFetchStatus: "idle",
    isAgeVerified: null,
    consentPurposes: [],
    shippingSelectedAddressId: undefined,
    accountContact: undefined,
    isSelectShippingAddress: false,
    placingOrder: false,
    paymentToken: "",
    isPaymentMethodsListLoaded: false,
    pgsPaymentMethods: [],
    pgsClient: "",
    isPgsClientPending: false,
    loadedPgsClient: "",
    pgsAdapter: {
        client: "",
        paymentCode: "",
    },
    setShippingMethodsOnCartPending: false,
    updateAgeVerificationPending: false,
    isAgeVerificationSuccess: false,
    error: {
        visible: false,
        exception: null,
    },
};
export const checkout = createSlice({
    name: "checkout",
    initialState: initialCheckoutState,
    reducers: {
        setCountry(state, action) {
            state.country = action.payload;
        },
        toggleSelectShippingAddress(state, action) {
            state.isSelectShippingAddress = action.payload;
        },
        clearShippingSelectedAddressId(state) {
            state.shippingSelectedAddressId = undefined;
        },
        setShippingSelectedAddressId(state, action) {
            var _a, _b;
            state.shippingSelectedAddressId =
                (_a = action.payload) !== null && _a !== void 0 ? _a : (_b = state.addresses.data.find((address) => address.isDefaultShipping)) === null || _b === void 0 ? void 0 : _b.id;
        },
        updateOrderAttributes(state, action) {
            state.orderAttributes.data = state.orderAttributes.data.map((item) => {
                const isOrderAttributeFound = Object.prototype.hasOwnProperty.call(action.payload, item.code);
                if (isOrderAttributeFound) {
                    return Object.assign(Object.assign({}, item), { value: action.payload[item.code] });
                }
                return item;
            });
        },
        updateTradeInOrderAttributes(state, action) {
            state.tradeInAttributes = action.payload;
        },
        showError(state, action) {
            state.error.exception = action.payload;
            state.error.visible = true;
        },
        hideError(state) {
            state.error.exception = null;
            state.error.visible = false;
        },
        setIsPgsClientPending(state, action) {
            state.isPgsClientPending = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getDefaultShippingMethods.pending, (state) => {
            state.shippingMethods.fetchStatus = "pending";
        });
        builder.addCase(getDefaultShippingMethods.rejected, (state) => {
            state.shippingMethods.fetchStatus = "error";
        });
        builder.addCase(getDefaultShippingMethods.fulfilled, (state, action) => {
            const { methods } = action.payload;
            state.shippingMethods.data = methods;
            state.shippingMethods.fetchStatus = "success";
        });
        builder.addCase(fetchAddresses.pending, (state) => {
            state.addresses.fetchStatus = "pending";
        });
        builder.addCase(fetchAddresses.rejected, (state) => {
            state.addresses.fetchStatus = "error";
        });
        builder.addCase(fetchAddresses.fulfilled, (state, action) => {
            const { addresses } = action.payload;
            state.addresses.fetchStatus = "success";
            state.addresses.data = addresses;
        });
        builder.addCase(fetchOrderDeliveryLimits.pending, (state) => {
            state.orderDeliveryLimits.fetchStatus = "pending";
        });
        builder.addCase(fetchOrderDeliveryLimits.fulfilled, (state, action) => {
            state.orderDeliveryLimits.data = action.payload;
            state.orderDeliveryLimits.fetchStatus = "success";
        });
        builder.addCase(fetchOrderDeliveryLimits.rejected, (state) => {
            state.orderDeliveryLimits.fetchStatus = "error";
        });
        builder.addCase(fetchOrderAttributes.pending, (state) => {
            state.orderDeliveryLimits.fetchStatus = "pending";
        });
        builder.addCase(fetchOrderAttributes.fulfilled, (state, action) => {
            state.orderAttributes.data = action.payload;
            state.orderAttributes.fetchStatus = "success";
        });
        builder.addCase(fetchOrderAttributes.rejected, (state) => {
            state.orderAttributes.fetchStatus = "error";
        });
        builder.addCase(fetchAccountContact.fulfilled, (state, action) => {
            const { personalInfo: { firstname, lastname, telephone, dob }, is_age_verified, purposes, } = action.payload;
            state.accountContact = { firstname, lastname, telephone, dob };
            state.isAgeVerified = is_age_verified;
            state.consentPurposes = purposes;
            state.profileDataFetchStatus = "success";
        });
        builder.addCase(fetchAccountContact.pending, (state) => {
            state.profileDataFetchStatus = "pending";
        });
        builder.addCase(fetchAccountContact.rejected, (state) => {
            state.profileDataFetchStatus = "error";
        });
        builder.addCase(placeOrder.pending, (state) => {
            state.placingOrder = true;
        });
        builder.addCase(placeOrder.fulfilled, (state, action) => {
            state.placingOrder = false;
            state.selectedAddressForPayment = action.payload.shippingAddress;
        });
        builder.addCase(placeOrder.rejected, (state, action) => {
            state.placingOrder = false;
            state.error.exception = action.payload || null;
            state.error.visible = true;
        });
        builder.addCase(getPGSToken.pending, (state) => {
            state.placingOrder = true;
        });
        builder.addCase(getPGSToken.fulfilled, (state, action) => {
            state.placingOrder = false;
            state.paymentToken = action.payload;
        });
        builder.addCase(getPGSToken.rejected, (state, action) => {
            state.error.exception = action.payload || null;
            state.error.visible = true;
        });
        builder.addCase(getPgsPaymentMethods.fulfilled, (state, action) => {
            state.pgsPaymentMethods = action.payload.data;
            state.isPaymentMethodsListLoaded = true;
        });
        builder.addCase(getPgsClient.fulfilled, (state, action) => {
            state.pgsClient = action.payload;
        });
        builder.addCase(getPgsAdapter.pending, (state) => {
            state.isPgsClientPending = true;
        });
        builder.addCase(getPgsAdapter.fulfilled, (state, action) => {
            state.pgsAdapter = action.payload;
        });
        builder.addCase(loadPgsAdapter.fulfilled, (state, action) => {
            state.loadedPgsClient = action.payload;
            state.isPgsClientPending = false;
        });
        builder.addCase(setShippingMethodsOnCart.pending, (state) => {
            state.setShippingMethodsOnCartPending = true;
        });
        builder.addCase(setShippingMethodsOnCart.fulfilled, (state) => {
            state.setShippingMethodsOnCartPending = false;
        });
        builder.addCase(updateAgeVerification.pending, (state) => {
            state.updateAgeVerificationPending = true;
        });
        builder.addCase(updateAgeVerification.fulfilled, (state, action) => {
            const { is_age_verified } = action.payload;
            state.updateAgeVerificationPending = false;
            state.isAgeVerified = is_age_verified;
            state.isAgeVerificationSuccess = is_age_verified;
        });
    },
});
export const { setCountry, toggleSelectShippingAddress, setShippingSelectedAddressId, clearShippingSelectedAddressId, updateOrderAttributes, updateTradeInOrderAttributes, showError, hideError, setIsPgsClientPending, } = checkout.actions;
//# sourceMappingURL=checkout.js.map